import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../../../stores/authStore';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { useTranslation } from '../../../i18n';
import { Pagination, usePagination } from '../../../components/ui/Pagination';

interface AnalyticsData {
  metrics: {
    totalScenarios: number;
    publishedScenarios: number;
    totalAttempts: number;
    completedAttempts: number;
    inProgressAttempts: number;
    passRate: number;
    averageScore: number;
  };
  scenarioStats: Array<{
    id: string;
    title: string;
    category: string;
    totalAttempts: number;
    completedCount: number;
    averageScore: number;
    passRate: number;
  }>;
  recentAttempts: Array<{
    id: string;
    simulationId: string;
    scenarioTitle: string;
    userId: string;
    learnerName: string;
    learnerEmail: string;
    employeeId: string;
    team: string;
    designation: string;
    tenantId?: string;
    tenantName?: string;
    status: string;
    score: number | null;
    isPassed: boolean | null;
    elapsedSeconds?: number | null;
    startedAt: string;
    completedAt: string | null;
  }>;
  tenants?: Array<{ id: string; name: string; slug: string }>;
}

const AVATAR_COLORS = ['#3B5BDB', '#0F8A5F', '#B26A00', '#C2477A', '#6A3FD1', '#0E7C86', '#4B5568', '#2F68C9'];

export function AdminAnalyticsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPlatformAdmin } = usePermissions();

  const [data, setData] = useState<AnalyticsData | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(isPlatformAdmin ? 'all' : (user?.tenantId || ''));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [attemptSearch, setAttemptSearch] = useState('');

  // Searchable Org Filter state
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [orgSearchText, setOrgSearchText] = useState('');
  const orgDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAnalytics(selectedTenantId);
  }, [selectedTenantId]);

  // Click outside listener for org dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target as Node)) {
        setIsOrgDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function fetchAnalytics(tenantIdScope?: string) {
    try {
      setIsLoading(true);
      setError(null);
      const queryParams = tenantIdScope && tenantIdScope !== 'all' ? `?tenantId=${encodeURIComponent(tenantIdScope)}` : (isPlatformAdmin ? '?tenantId=all' : '');
      const res = await api.get<AnalyticsData>(`/api/v1/admin/analytics${queryParams}`);
      if (res && res.data) {
        setData(res.data);
      } else if (res && (res as any).error) {
        setError((res as any).error.message || 'Failed to load enterprise analytics.');
      } else {
        // Fallback default structure
        setData({
          metrics: {
            totalScenarios: 0,
            publishedScenarios: 0,
            totalAttempts: 0,
            completedAttempts: 0,
            inProgressAttempts: 0,
            passRate: 0,
            averageScore: 0,
          },
          scenarioStats: [],
          recentAttempts: [],
          tenants: [],
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load enterprise analytics.');
    } finally {
      setIsLoading(false);
    }
  }

  const tenantsList = useMemo(() => {
    return data?.tenants || [];
  }, [data?.tenants]);

  const filteredTenants = useMemo(() => {
    if (!orgSearchText.trim()) return tenantsList;
    const q = orgSearchText.toLowerCase();
    return tenantsList.filter(
      (t) => t.name.toLowerCase().includes(q) || (t.slug && t.slug.toLowerCase().includes(q)) || t.id.toLowerCase().includes(q)
    );
  }, [tenantsList, orgSearchText]);

  const currentTenantObj = useMemo(() => {
    if (!selectedTenantId || selectedTenantId === 'all') return null;
    return tenantsList.find((t) => t.id === selectedTenantId) || null;
  }, [tenantsList, selectedTenantId]);

  const filteredScenarios = useMemo(() => {
    return (data?.scenarioStats || []).filter(
      (s) =>
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.category.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [data?.scenarioStats, searchQuery]);

  const filteredAttempts = useMemo(() => {
    return (data?.recentAttempts || []).filter((a) => {
      if (!attemptSearch) return true;
      const q = attemptSearch.toLowerCase();
      return (
        a.learnerName.toLowerCase().includes(q) ||
        a.learnerEmail.toLowerCase().includes(q) ||
        a.employeeId.toLowerCase().includes(q) ||
        a.scenarioTitle.toLowerCase().includes(q) ||
        a.id.toLowerCase().includes(q)
      );
    });
  }, [data?.recentAttempts, attemptSearch]);

  const {
    currentPage: scnPage,
    pageSize: scnPageSize,
    totalItems: scnTotalItems,
    paginatedItems: paginatedScenarios,
    goToPage: setScnPage,
    setPageSize: setScnPageSize,
  } = usePagination(filteredScenarios, 1, 20);

  const {
    currentPage: attPage,
    pageSize: attPageSize,
    totalItems: attTotalItems,
    paginatedItems: paginatedAttempts,
    goToPage: setAttPage,
    setPageSize: setAttPageSize,
  } = usePagination(filteredAttempts, 1, 20);

  const formatDuration = (seconds?: number | null) => {
    if (!seconds) return '—';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}m ${s}s`;
  };

  return (
    <div className="admin-page">
      {/* Standardized Admin Header & Banner */}
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Training Analytics' },
        ]}
        icon="📊"
        title="Enterprise Training Analytics & Insights"
        badge={
          isPlatformAdmin
            ? (selectedTenantId === 'all' ? 'Super Admin · Global Scope' : `Filtered: ${currentTenantObj?.name || 'Organization'}`)
            : 'Tenant Scope'
        }
        description="Real-time simulation completion metrics, competency pass rates, learner drilldowns, and multi-tenant performance."
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {/* Searchable Organization Dropdown for Super Admin */}
            {isPlatformAdmin && (
              <div ref={orgDropdownRef} style={{ position: 'relative', minWidth: '240px' }}>
                <button
                  type="button"
                  onClick={() => setIsOrgDropdownOpen(!isOrgDropdownOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    width: '100%',
                    height: '38px',
                    padding: '0 12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#FFFFFF',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span>{selectedTenantId === 'all' ? '🌐' : '🏢'}</span>
                    <span>{selectedTenantId === 'all' ? 'All Organizations' : (currentTenantObj?.name || selectedTenantId)}</span>
                  </div>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '14px', height: '14px', opacity: 0.8, flexShrink: 0 }}>
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>

                {isOrgDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      minWidth: '280px',
                      background: '#FFFFFF',
                      border: '1px solid var(--line, #E2E8F0)',
                      borderRadius: '10px',
                      boxShadow: '0 10px 30px rgba(7, 29, 69, 0.15)',
                      zIndex: 50,
                      padding: '8px',
                      color: '#0F172A',
                    }}
                  >
                    <div style={{ marginBottom: '6px' }}>
                      <input
                        type="text"
                        placeholder="Search organization..."
                        value={orgSearchText}
                        onChange={(e) => setOrgSearchText(e.target.value)}
                        autoFocus
                        style={{
                          width: '100%',
                          height: '32px',
                          padding: '0 8px',
                          fontSize: '12.5px',
                          border: '1px solid var(--line, #E2E8F0)',
                          borderRadius: '6px',
                          outline: 'none',
                        }}
                      />
                    </div>
                    <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                      <div
                        onClick={() => {
                          setSelectedTenantId('all');
                          setIsOrgDropdownOpen(false);
                        }}
                        style={{
                          padding: '8px 10px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: selectedTenantId === 'all' ? 600 : 400,
                          color: selectedTenantId === 'all' ? 'var(--blue, #3B5BDB)' : 'var(--ink, #1E293B)',
                          background: selectedTenantId === 'all' ? 'var(--blue-soft, #EEF2FF)' : 'transparent',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <span>🌐</span>
                        <span>All Organizations (Global Scope)</span>
                      </div>

                      {filteredTenants.map((t) => (
                        <div
                          key={t.id}
                          onClick={() => {
                            setSelectedTenantId(t.id);
                            setIsOrgDropdownOpen(false);
                          }}
                          style={{
                            padding: '8px 10px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: selectedTenantId === t.id ? 600 : 400,
                            color: selectedTenantId === t.id ? 'var(--blue, #3B5BDB)' : 'var(--ink, #1E293B)',
                            background: selectedTenantId === t.id ? 'var(--blue-soft, #EEF2FF)' : 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                            <span>🏢</span>
                            <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{t.name}</span>
                          </div>
                          {t.slug && <span style={{ fontSize: '11px', color: 'var(--muted, #64748B)', background: 'var(--surface, #F8FAFC)', padding: '1px 5px', borderRadius: '4px' }}>{t.slug}</span>}
                        </div>
                      ))}

                      {filteredTenants.length === 0 && (
                        <div style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: 'var(--muted, #64748B)' }}>
                          No organizations matching "{orgSearchText}"
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => fetchAnalytics(selectedTenantId)}
              className="admin-btn admin-btn--secondary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                height: '38px',
                background: 'rgba(255, 255, 255, 0.15)',
                color: '#FFFFFF',
                borderColor: 'rgba(255, 255, 255, 0.3)',
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '14px', height: '14px' }}>
                <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
                <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                <path d="M16 16h5v5" />
              </svg>
              Refresh Data
            </button>
          </div>
        }
      />

      {/* Loading State */}
      {isLoading && (
        <div className="admin-loading-state" style={{ padding: '60px 20px', textAlign: 'center' }}>
          <div className="dashboard-spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--muted, #64748B)', fontSize: '14px', fontWeight: 500 }}>
            Aggregating performance analytics & attempt records…
          </p>
        </div>
      )}

      {/* Error Banner */}
      {!isLoading && error && (
        <div className="admin-alert admin-alert--error" style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <strong>Analytics Notice: </strong>
            <span>{error}</span>
          </div>
          <button
            onClick={() => fetchAnalytics(selectedTenantId)}
            className="admin-btn admin-btn--secondary"
            style={{ padding: '4px 12px', fontSize: '12px' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Main Analytics Content */}
      {!isLoading && data && (
        <>
          {/* KPI Stats Cards */}
          <div className="admin-stats-grid" style={{ marginBottom: '24px' }}>
            <div className="admin-stat-card">
              <div className="admin-stat-card__icon" style={{ background: '#EEF2FF', color: '#3B5BDB' }}>📊</div>
              <div className="admin-stat-card__content">
                <span className="admin-stat-card__label">{t('analytics.stats.total_attempts', undefined, 'Total Simulation Attempts')}</span>
                <span className="admin-stat-card__value">{data.metrics.totalAttempts}</span>
                <span style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                  {data.metrics.completedAttempts} {t('status.completed', undefined, 'completed')} · {data.metrics.inProgressAttempts} {t('status.in_progress', undefined, 'in progress')}
                </span>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-card__icon" style={{ background: '#F0FDF4', color: '#0F8A5F' }}>
                🎯
              </div>
              <div className="admin-stat-card__content">
                <span className="admin-stat-card__label">{t('analytics.stats.avg_score', undefined, 'Average Proficiency Score')}</span>
                <span className="admin-stat-card__value" style={{ color: data.metrics.averageScore >= 70 ? '#0F8A5F' : '#D97706' }}>
                  {data.metrics.averageScore}%
                </span>
                <span style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                  {t('analytics.stats.avg_score_sub', undefined, 'Across all scored evaluation attempts')}
                </span>
              </div>
            </div>

            <div className="admin-stat-card">
              <div
                className="admin-stat-card__icon"
                style={{
                  background: data.metrics.passRate >= 70 ? '#ECFDF5' : '#FFFBEB',
                  color: data.metrics.passRate >= 70 ? '#059669' : '#D97706',
                }}
              >
                {data.metrics.passRate >= 70 ? '✅' : '⚠️'}
              </div>
              <div className="admin-stat-card__content">
                <span className="admin-stat-card__label">{t('analytics.stats.pass_rate', undefined, 'Benchmark Pass Rate')}</span>
                <span
                  className="admin-stat-card__value"
                  style={{ color: data.metrics.passRate >= 70 ? '#059669' : '#D97706' }}
                >
                  {data.metrics.passRate}%
                </span>
                <span style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                  {t('analytics.stats.pass_rate_sub', undefined, 'Standard threshold benchmark: 70%')}
                </span>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-card__icon" style={{ background: '#F8FAFC', color: '#0F172A' }}>
                🎬
              </div>
              <div className="admin-stat-card__content">
                <span className="admin-stat-card__label">{t('analytics.stats.active_scenarios', undefined, 'Active Scenarios')}</span>
                <span className="admin-stat-card__value">{data.metrics.publishedScenarios}</span>
                <span style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>
                  {data.metrics.totalScenarios} {t('analytics.stats.total_scenarios_sub', undefined, 'total designed cases')}
                </span>
              </div>
            </div>
          </div>

          {/* Scenario Performance Table */}
          <div className="admin-section" style={{ background: '#FFFFFF', border: '1px solid var(--line, #E2E8F0)', borderRadius: '12px', padding: '20px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h2 className="admin-section__title" style={{ fontSize: '17px', margin: 0 }}>Scenario Performance Breakdown</h2>
                <p style={{ margin: '3px 0 0', fontSize: '13px', color: 'var(--muted, #64748B)' }}>
                  Detailed completion count, average scores, and accuracy statistics grouped by simulation scenario.
                </p>
              </div>
              <input
                type="text"
                placeholder="Search scenario or category…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="admin-form__input"
                style={{ width: '260px', height: '36px', fontSize: '13px' }}
              />
            </div>

            <div className="admin-table-container" style={{ border: 'none', boxShadow: 'none', marginTop: '14px' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Scenario Title</th>
                    <th>Category</th>
                    <th>Attempts</th>
                    <th>Completed</th>
                    <th>Avg Score</th>
                    <th>Pass Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedScenarios.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <Link
                          to={`/scenarios/${s.id}`}
                          style={{ color: 'var(--blue, #3B5BDB)', textDecoration: 'none', fontWeight: 600 }}
                        >
                          {s.title}
                        </Link>
                      </td>
                      <td>
                        <span style={{ fontSize: '12px', background: 'var(--surface, #F8FAFC)', padding: '2px 8px', borderRadius: '4px', border: '1px solid var(--line, #E2E8F0)' }}>
                          {s.category}
                        </span>
                      </td>
                      <td><b>{s.totalAttempts}</b></td>
                      <td>{s.completedCount}</td>
                      <td>
                        {s.averageScore > 0 ? (
                          <strong style={{ color: s.averageScore >= 70 ? 'var(--ok, #0F8A5F)' : 'var(--warn, #D97706)' }}>
                            {s.averageScore}%
                          </strong>
                        ) : (
                          <span style={{ color: 'var(--muted, #64748B)' }}>—</span>
                        )}
                      </td>
                      <td>
                        {s.completedCount > 0 ? (
                          <span
                            className={`admin-status-badge ${
                              s.passRate >= 70 ? 'admin-status-badge--active' : 'admin-status-badge--suspended'
                            }`}
                          >
                            {s.passRate}%
                          </span>
                        ) : (
                          <span style={{ color: 'var(--muted, #64748B)' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredScenarios.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ padding: '36px', textAlign: 'center', color: 'var(--muted, #64748B)' }}>
                        No scenario statistics matching search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {scnTotalItems > 0 && (
                <Pagination
                  currentPage={scnPage}
                  totalItems={scnTotalItems}
                  pageSize={scnPageSize}
                  onPageChange={setScnPage}
                  onPageSizeChange={setScnPageSize}
                  showPageSizeSelector
                  pageSizeOptions={[10, 20, 50, 100]}
                  itemLabel="scenarios"
                />
              )}
            </div>
          </div>

          {/* Recent Activity Table with Learner Details */}
          <div className="admin-section" style={{ background: '#FFFFFF', border: '1px solid var(--line, #E2E8F0)', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h2 className="admin-section__title" style={{ fontSize: '17px', margin: 0 }}>Recent Simulation Attempts & Learner Activity</h2>
                <p style={{ margin: '3px 0 0', fontSize: '13px', color: 'var(--muted, #64748B)' }}>
                  Individual attempt records with evaluation scores, duration, and direct report access.
                </p>
              </div>
              <input
                type="text"
                placeholder="Search learner, email, ID or scenario…"
                value={attemptSearch}
                onChange={(e) => setAttemptSearch(e.target.value)}
                className="admin-form__input"
                style={{ width: '280px', height: '36px', fontSize: '13px' }}
              />
            </div>

            <div className="admin-table-container" style={{ border: 'none', boxShadow: 'none', marginTop: '14px' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Learner</th>
                    <th>Scenario</th>
                    {isPlatformAdmin && <th>Organization</th>}
                    <th>Duration</th>
                    <th>Score</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttempts.length === 0 ? (
                    <tr>
                      <td colSpan={isPlatformAdmin ? 8 : 7} style={{ padding: '36px', textAlign: 'center', color: 'var(--muted, #64748B)' }}>
                        No simulation attempts recorded for the selected scope.
                      </td>
                    </tr>
                  ) : (
                    paginatedAttempts.map((att, idx) => {
                      const initials = `${(att.learnerName.split(' ')[0]?.[0] || '').toUpperCase()}${(att.learnerName.split(' ')[1]?.[0] || '').toUpperCase()}` || 'U';
                      const color = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                      return (
                        <tr key={att.id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  background: color,
                                  color: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 700,
                                  fontSize: '12px',
                                  flexShrink: 0,
                                }}
                              >
                                {initials}
                              </span>
                              <div>
                                <b style={{ display: 'block', color: 'var(--navy, #0F172A)', fontSize: '13.5px' }}>{att.learnerName}</b>
                                <span style={{ fontSize: '11.5px', color: 'var(--muted, #64748B)' }}>
                                  {att.employeeId} · {att.team}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <Link
                              to={`/scenarios/${att.simulationId}`}
                              style={{ color: 'var(--blue, #3B5BDB)', textDecoration: 'none', fontWeight: 600, fontSize: '13px' }}
                            >
                              {att.scenarioTitle}
                            </Link>
                          </td>
                          {isPlatformAdmin && (
                            <td>
                              <span style={{ fontSize: '12px', background: 'var(--surface, #F8FAFC)', padding: '2px 8px', borderRadius: '4px', border: '1px solid var(--line, #E2E8F0)' }}>
                                {att.tenantName || '—'}
                              </span>
                            </td>
                          )}
                          <td style={{ fontSize: '12.5px', color: 'var(--muted, #64748B)' }}>
                            {formatDuration(att.elapsedSeconds)}
                          </td>
                          <td>
                            {att.score !== null ? (
                              <strong style={{ color: att.score >= 70 ? 'var(--ok, #0F8A5F)' : 'var(--warn, #D97706)', fontSize: '14px' }}>
                                {att.score}%
                              </strong>
                            ) : (
                              <span style={{ color: 'var(--muted, #64748B)' }}>—</span>
                            )}
                          </td>
                          <td>
                            <span
                              className={`admin-status-badge ${
                                att.isPassed
                                  ? 'admin-status-badge--active'
                                  : att.status === 'COMPLETED' || att.status === 'SUBMITTED' || att.status === 'EVALUATED' || att.status === 'SCORED'
                                  ? 'admin-status-badge--suspended'
                                  : 'admin-status-badge--pending'
                              }`}
                            >
                              {att.isPassed ? 'PASSED' : att.status === 'IN_PROGRESS' ? 'IN PROGRESS' : (att.score !== null ? 'FAILED' : att.status)}
                            </span>
                          </td>
                          <td style={{ fontSize: '12.5px', color: 'var(--muted, #64748B)', whiteSpace: 'nowrap' }}>
                            {new Date(att.startedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </td>
                          <td>
                            <button
                              type="button"
                              onClick={() => navigate(`/attempts/${att.id}/results`)}
                              className="admin-btn admin-btn--secondary"
                              style={{ height: '30px', fontSize: '12px', padding: '0 10px' }}
                            >
                              View Report
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              {attTotalItems > 0 && (
                <Pagination
                  currentPage={attPage}
                  totalItems={attTotalItems}
                  pageSize={attPageSize}
                  onPageChange={setAttPage}
                  onPageSizeChange={setAttPageSize}
                  showPageSizeSelector
                  pageSizeOptions={[10, 20, 50, 100]}
                  itemLabel="attempts"
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
